/* eslint-disable max-lines */
import React from 'react';
import { withTheme } from 'styled-components';
import { styled } from '@this/constants/themes';
import TranslateIgnoreText from '@this/components/shared/text/translate_ignore_text';
import { media } from '@this/components/shared/atoms/media';
import InAdvanceApprovalStatus from '@this/components/shared/in_advance_approval_status/in_advance_approval_status';
import type Department from '@this/domain/department/department';
import type Project from '@this/domain/project/project';
import { Fetcher } from '@this/src/util';
import type OrderItem from '@this/domain/order_item2';
import CancelItemModal from '@this/components/trips_management/trips/trips_chat/cancel_item_modal';
import OrderItemContent from '@this/components/trips_management/trips/order_item_content';
import type OrderItemCalendarEvent from '@this/src/domain/order_item_calendar_event';
import type Trip from '@this/domain/trip/trip2';
import type TravelerNameChange from '@this/domain/trip/traveler_name_change';
import Notification from '../../../notification';
import TripCardHead from './trip_card_head';
import TripCardRightTemplate from './trip_card_right.template';
import TicketingTripModal from './ticketing_trip_modal';
import CancelTripModal from './cancel_trip_modal';
import RequestApprovalModal from './request_approval_modal/request_approval_modal';
import FlightSeatMapModal from './flight_seat_map_modal';
import TripCardLeft from './trip_card_left';
import CalendarSuccessModal from './calendar/calendar_success_modal';

import moment from '../../../lib/moment';

interface Props {
  trip: Trip;
  user: any;
  service: { name: string; tel: string };
  departments: Department[];
  projects: Project[];
  tripReportAvailable: boolean;
  openDraftSearch: (trip: Trip) => void;
  handleDownloadClick: (id: number, e: React.MouseEvent<HTMLElement>) => void;
  handleCancelClick: (id: number, e: React.MouseEvent<HTMLElement>) => void;
  handleExicClick: (id: number, exicUri: string, e: React.MouseEvent<HTMLElement>) => void;
  handleBusinessEkinetClick: (id: number, businessEkinetUri: string, e: React.MouseEvent<HTMLElement>) => void;
  showConfirmationModal?: (trip: Trip) => void;
  serviceId: number;
  fetchTrips: () => void;
  theme: Theme;
  isReceipt?: boolean;
}

interface State {
  showTicketingConfirm: boolean;
  ticketingRequesting: boolean;
  showCancelConfirm: boolean;
  showItemCancelConfirm: boolean;
  showRequestApproval: boolean;
  showFlightSeatMapModal: boolean;
  cancelTargetItem: OrderItem | null;
  canceling: boolean;
  registeredCalendarEvents: { [key: string]: OrderItemCalendarEvent };
}

interface Theme {
  themeClass: string;
  serviceName: string;
}

class TripCard extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showTicketingConfirm: false,
      ticketingRequesting: false,
      showCancelConfirm: false,
      showItemCancelConfirm: false,
      showRequestApproval: false,
      showFlightSeatMapModal: false,
      cancelTargetItem: null,
      canceling: false,
      registeredCalendarEvents: {}
    };
  }

  showTicketingModal = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    return this.setState({ showTicketingConfirm: true });
  };

  showDisableNotification = () => {
    Notification.error('この海外航空券は発券期限が過ぎています。\n手配オペレーターまでチャットでご連絡ください。');
  };

  hideTicketingModal = () => {
    return this.setState({ showTicketingConfirm: false });
  };

  sendTicketingRequest = async (e?: React.MouseEvent<HTMLAnchorElement>) => {
    if (e) {
      e.stopPropagation();
    }
    this.setState({
      ticketingRequesting: true
    });

    try {
      await Fetcher.post(`/trips/${this.props.trip.id}/ticketing_requests.json`, {});
      this.setState({
        ticketingRequesting: false,
        showTicketingConfirm: false
      });
      this.props.fetchTrips();
    } catch {
      Notification.error('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
    }
  };

  showCancelModal = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    return this.setState({ showCancelConfirm: true });
  };

  showItemCancelModal = (orderItem: OrderItem) => {
    return this.setState({ showItemCancelConfirm: true, cancelTargetItem: orderItem });
  };

  showRequestApprovalModal = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    return this.setState({ showRequestApproval: true });
  };

  hideCancelModal = () => {
    return this.setState({ showCancelConfirm: false });
  };

  hideRequestApprovalModal = () => {
    return this.setState({ showRequestApproval: false });
  };

  hideItemCancelModal = () => {
    return this.setState({ showItemCancelConfirm: false, cancelTargetItem: null });
  };

  submitRequestApproval = async (e?: React.MouseEvent<HTMLAnchorElement>) => {
    if (e) {
      e.stopPropagation();
    }
    try {
      this.setState({
        showRequestApproval: false
      });
      this.props.fetchTrips();
    } catch (e) {
      Notification.error('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
    }
  };

  handleCancelReservation = async (e?: React.MouseEvent<HTMLAnchorElement>) => {
    if (e) {
      e.stopPropagation();
    }
    this.setState({
      canceling: true
    });

    const params = {
      trip_id: this.props.trip.id,
      type: 'user'
    };

    try {
      await Fetcher.delete('/trips/cancel.json', params);
      this.setState({
        canceling: false,
        showCancelConfirm: false
      });
      this.props.fetchTrips();
    } catch {
      Notification.error('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
    }
  };

  handleCancelItemReservation = async (e: React.MouseEvent<HTMLButtonElement>, orderItemId: number) => {
    if (e) {
      e.stopPropagation();
    }
    this.setState({
      canceling: true
    });

    const params = {
      id: orderItemId
    };

    try {
      await Fetcher.delete('/order_items/cancel.json', params);
      this.setState({
        canceling: false,
        showItemCancelConfirm: false
      });
      this.props.fetchTrips();
    } catch {
      Notification.error('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
    } finally {
      this.setState({
        canceling: false
      });
    }
  };

  endTime(): Date | null {
    const { trip } = this.props;
    const endTime = trip.cacheEndTime || trip.order?.endTime;
    if (endTime) {
      return moment(endTime).toDate();
    }
    return null;
  }

  getChargingDepartmentNames = () => {
    const { trip } = this.props;

    if (trip.departments && trip.departments.length > 0) {
      return trip.departments.map(data => (
        <div key={data.id} data-wovn-ignore>
          {data.code}:{data.name}
        </div>
      ));
    }

    if (trip?.order?.orderItems && trip.order.orderItems.length > 0) {
      return (
        trip.order.orderItems &&
        trip.order.orderItems.map((orderItem, i: number) => {
          if (orderItem.orderItemMappings.some(mapping => mapping.chargingDepartmentId == null)) {
            return <div key={`order-item-empty-${orderItem.id}`} />;
          }

          return (
            <div key={`order-item-${orderItem.id}`}>
              <div data-wovn-ignore>
                {i + 1}. {orderItem?.orderItemCategoryText}
              </div>
              <div style={{ marginLeft: '10px' }}>
                {orderItem.orderItemMappings.map(mapping => (
                  <div key={`mapping-${mapping.chargingDepartmentId}`} data-wovn-ignore>
                    {mapping.displayChargingDepartment}
                  </div>
                ))}
              </div>
            </div>
          );
        })
      );
    }

    // trip.non_order_itemsが存在する場合
    if (trip?.nonOrderItems && trip.nonOrderItems.length > 0) {
      return trip.nonOrderItems.map((nonOrderItem, i: number) => {
        if (nonOrderItem.nonOrderItemMappings.every(mapping => mapping.chargingDepartmentId == null)) {
          return <div key={`non-order-item-empty-${nonOrderItem.id}`} />;
        }

        const mappingsToShow = trip.showDepartmentDetail
          ? nonOrderItem.nonOrderItemMappings
          : [nonOrderItem.nonOrderItemMappings[0]];
        return (
          <div key={`order-item-${nonOrderItem.id}`}>
            <div data-wovn-ignore>
              {i + 1}. {nonOrderItem?.hotels ? 'ホテル' : '経路'}
            </div>
            <div style={{ marginLeft: '10px' }}>
              {mappingsToShow.map(mapping => (
                <div key={`mapping-${mapping.chargingDepartmentId}`} data-wovn-ignore>
                  {mapping.displayChargingDepartment}
                </div>
              ))}
            </div>
          </div>
        );
      });
    }
    return null;
  };

  getProjectNames = () => {
    const { trip } = this.props;

    if (trip.projects && trip.projects.length > 0) {
      return trip.projects.map(data => (
        <div key={data.id} data-wovn-ignore>
          {data.code}:{data.name}
        </div>
      ));
    }

    if (trip.order && trip.order.orderItems && trip.order.orderItems.length > 0) {
      return (
        trip.order.orderItems &&
        trip.order.orderItems.map((orderItem, i: number) => {
          if (orderItem.orderItemMappings.some(mapping => mapping.projectId == null)) {
            return <div key={`order-item-project-empty-${orderItem.id}`} />;
          }

          return (
            <div key={`order-item-${orderItem.id}`}>
              <div data-wovn-ignore>
                {i + 1}. {orderItem?.orderItemCategoryText}
              </div>
              <div style={{ marginLeft: '10px' }}>
                {orderItem.orderItemMappings.map(mapping => (
                  <div key={`mapping-${mapping.projectId}`} data-wovn-ignore>
                    {/* TODO: 動作確認 */}
                    {mapping.displayProject}
                  </div>
                ))}
              </div>
            </div>
          );
        })
      );
    }

    // trip.non_order_itemsが存在する場合
    if (trip.nonOrderItems && trip.nonOrderItems.length > 0) {
      return trip.nonOrderItems.map((nonOrderItem, i: number) => {
        if (nonOrderItem.nonOrderItemMappings.every(mapping => mapping.projectId == null)) {
          return <div key={`non-order-item-project-empty-${nonOrderItem.id}`} />;
        }

        const mappingsToShow = trip.showProjectDetail
          ? nonOrderItem.nonOrderItemMappings
          : [nonOrderItem.nonOrderItemMappings[0]];
        return (
          <div key={`order-item-${nonOrderItem.id}`}>
            <div data-wovn-ignore>
              {i + 1}. {nonOrderItem?.hotels ? 'ホテル' : '経路'}
            </div>
            <div style={{ marginLeft: '10px' }}>
              {mappingsToShow.map(mapping => (
                <div key={`mapping-${mapping.projectId}`} data-wovn-ignore>
                  {mapping.displayChargingDepartment}
                </div>
              ))}
            </div>
          </div>
        );
      });
    }
    return null;
  };

  handleCalendarEventRegistered = (traceId: string, calendarEvent: OrderItemCalendarEvent) => {
    this.setState({
      registeredCalendarEvents: {
        ...this.state.registeredCalendarEvents,
        [traceId]: calendarEvent
      }
    });
  };

  render() {
    const {
      trip,
      user,
      service,
      departments,
      projects,
      tripReportAvailable,
      openDraftSearch,
      handleDownloadClick,
      handleCancelClick,
      handleExicClick,
      handleBusinessEkinetClick,
      showConfirmationModal,
      serviceId,
      theme,
      isReceipt
    } = this.props;

    const {
      showTicketingConfirm,
      ticketingRequesting,
      showCancelConfirm,
      showItemCancelConfirm,
      showRequestApproval,
      showFlightSeatMapModal,
      cancelTargetItem,
      canceling,
      registeredCalendarEvents
    } = this.state;
    const travelerNames: string[] = trip.travelerNames;
    const simpleRequestType = trip.nonOrderItems.some(item => item.requestType === 'simple_request');

    // 予約時と現在の出張者名を取得
    const travelerNameChanges = trip.travelerNameChanges || [];
    const hasTravelerNameChanges = travelerNameChanges.length > 0;
    const travelerNamesToRender = hasTravelerNameChanges
      ? travelerNameChanges.map((c: TravelerNameChange) => c.currentTravelerName || '')
      : travelerNames;

    return (
      <SimpleCardWrap>
        <SimpleCard key={trip.id}>
          <CardHead>
            <TripCardHead trip={trip} endTime={this.endTime()} serviceId={serviceId} />
          </CardHead>
          <SimpleCardBody>
            <CardMain>
              <CardContent>
                <TripCardLeft
                  trip={trip}
                  user={user}
                  theme={theme}
                  serviceId={serviceId}
                  simpleRequestType={simpleRequestType}
                  showItemCancelModal={this.showItemCancelModal}
                  hideFooter={false}
                  registeredCalendarEvents={registeredCalendarEvents}
                  isReceipt={isReceipt}
                />
                {trip.inAdvanceApprovals && (
                  <>
                    {trip.isRejected && (
                      <MainInfo>
                        <Bold>■却下理由</Bold>
                        <TranslateIgnoreText text={trip.rejectReason(trip.inAdvanceApprovals.length - 1)} />
                      </MainInfo>
                    )}
                    {trip.shinkansenDeadline && !trip.isApproved && (
                      <MainInfo>
                        <Red>
                          <span>新幹線・特急チケットは、</span>
                          <span data-wovn-ignore>{trip.shinkansenDeadline}</span>
                          <span>が承認期限です。</span>
                        </Red>
                      </MainInfo>
                    )}
                  </>
                )}
              </CardContent>
            </CardMain>
            <CardSub>
              <TripCardRightTemplate
                trip={trip}
                user={user}
                tripReportAvailable={tripReportAvailable}
                openDraftSearch={openDraftSearch}
                handleDownloadClick={handleDownloadClick}
                handleCancelClick={handleCancelClick}
                handleExicClick={handleExicClick}
                handleBusinessEkinetClick={handleBusinessEkinetClick}
                showConfirmationModal={showConfirmationModal}
                handleTicketingClick={this.showTicketingModal}
                handleTripCancelClick={this.showCancelModal}
                isReceipt={isReceipt}
                handleTripRequestApprovalClick={this.showRequestApprovalModal}
                handleTicketingDisableClick={this.showDisableNotification}
                handleShowFlightSeatMapModal={() => this.setState({ showFlightSeatMapModal: true })}
                simpleRequestType={simpleRequestType}
              />
              <SubBlock>
                <SubTitle>申請情報</SubTitle>
                <SubTable>
                  <tbody>
                    <tr>
                      <SubTdData>出張期間</SubTdData>
                      <SubTdColon>：</SubTdColon>
                      <SubTd>
                        <TranslateIgnoreText text={trip.dateRange} />
                      </SubTd>
                    </tr>
                    {trip.inAdvanceApprovals ? (
                      <tr>
                        <SubTdData>申請者</SubTdData>
                        <SubTdColon>：</SubTdColon>
                        <SubTd>
                          <TranslateIgnoreText text={trip.user?.name} />
                        </SubTd>
                      </tr>
                    ) : (
                      <tr />
                    )}
                    {travelerNamesToRender.map((name: string, i: number) => {
                      const travelerNameChange = hasTravelerNameChanges ? travelerNameChanges[i] : null;
                      const travelerKey = travelerNameChange?.originalTravelerName
                        ? `${travelerNameChange.originalTravelerName}-to-${travelerNameChange.currentTravelerName}`
                        : name;

                      return (
                        <tr key={travelerKey}>
                          {travelerNamesToRender.length > 1 ? (
                            <SubTdData>{`出張者${i + 1}`}</SubTdData>
                          ) : (
                            <SubTdData>出張者</SubTdData>
                          )}
                          <SubTdColon>：</SubTdColon>
                          <SubTd>
                            {travelerNameChange && travelerNameChange.travelerNameChanged ? (
                              <span>
                                <s>{travelerNameChange.originalTravelerName ?? ''}</s>{' '}
                                {travelerNameChange.currentTravelerName ?? ''}
                              </span>
                            ) : (
                              <span>{name}</span>
                            )}
                          </SubTd>
                        </tr>
                      );
                    })}
                    <tr>
                      <SubTdData>訪問先</SubTdData>
                      <SubTdColon>：</SubTdColon>
                      <SubTd>
                        <TranslateIgnoreText text={trip?.finalDestination || ''} />
                      </SubTd>
                    </tr>
                    <tr>
                      <SubTdData>目的</SubTdData>
                      <SubTdColon>：</SubTdColon>
                      <SubTd>
                        <TranslateIgnoreText text={trip?.purpose || ''} />
                      </SubTd>
                    </tr>
                    <tr>
                      <SubTdData>社内管理番号</SubTdData>
                      <SubTdColon>：</SubTdColon>
                      <SubTd>
                        <TranslateIgnoreText text={trip.internalNumber || ''} />
                      </SubTd>
                    </tr>
                    {trip.tripApproveItem &&
                      trip.tripApproveItem.json?.map(item => {
                        if (item.dataType === 'label') {
                          return null;
                        }
                        return (
                          <tr key={item.id}>
                            <SubTdData>
                              <span data-wovn-ignore>{item.userDisplayName}</span>
                            </SubTdData>
                            <SubTdColon>：</SubTdColon>
                            <SubTd>
                              {item.dataType !== 'calendar' &&
                                (item.valueCode ? (
                                  <span data-wovn-ignore>
                                    {item.valueCode}：{item.value}
                                  </span>
                                ) : (
                                  <span data-wovn-ignore>{item.value}</span>
                                ))}
                              {item.dataType === 'calendar' && (
                                <span data-wovn-ignore>
                                  {item.value ? moment(item.value).format('MM月DD日') : ''}
                                </span>
                              )}
                            </SubTd>
                          </tr>
                        );
                      })}
                    {departments && departments.length > 0 ? (
                      <tr>
                        <SubTdData>費用負担部署</SubTdData>
                        <SubTdColon>：</SubTdColon>
                        <SubTd data-wovn-ignore>{this.getChargingDepartmentNames()}</SubTd>
                      </tr>
                    ) : (
                      <tr />
                    )}
                    {projects && projects.length > 0 ? (
                      <tr>
                        <SubTdData>プロジェクトコード</SubTdData>
                        <SubTdColon>：</SubTdColon>
                        <SubTd data-wovn-ignore>{this.getProjectNames()}</SubTd>
                      </tr>
                    ) : (
                      <tr />
                    )}
                  </tbody>
                </SubTable>
              </SubBlock>
              {trip.inAdvanceApprovals && trip.inAdvanceApprovals.length > 0 ? (
                <SubBlock>
                  <SubTitle>承認ステータス</SubTitle>
                  <InAdvanceApprovalStatus
                    trip={trip}
                    applicant={trip.applicant}
                    currentStatus={trip.currentApprovalStatus || 0}
                  />
                </SubBlock>
              ) : (
                ''
              )}
            </CardSub>
          </SimpleCardBody>
        </SimpleCard>
        <TicketingTripModal
          show={showTicketingConfirm}
          submitting={ticketingRequesting}
          handleHide={this.hideTicketingModal}
          handleSubmit={this.sendTicketingRequest}
        >
          <TripCardLeft
            trip={trip}
            user={user}
            theme={theme}
            serviceId={serviceId}
            simpleRequestType={simpleRequestType}
            showItemCancelModal={this.showItemCancelModal}
            hideFooter={false}
            registeredCalendarEvents={registeredCalendarEvents}
            isReceipt={isReceipt}
          />
        </TicketingTripModal>
        <CancelItemModal
          show={showItemCancelConfirm}
          submitting={canceling}
          trip={trip}
          orderItem={cancelTargetItem}
          service={service}
          handleHide={this.hideItemCancelModal}
          handleSubmit={this.handleCancelItemReservation}
          theme={this.props.theme}
        >
          {cancelTargetItem && (
            <OrderItemContent
              orderItem={cancelTargetItem}
              trip={trip}
              user={user}
              serviceId={serviceId}
              theme={this.props.theme}
            />
          )}
        </CancelItemModal>
        <CancelTripModal
          show={showCancelConfirm}
          submitting={canceling}
          trip={trip}
          service={service}
          handleHide={this.hideCancelModal}
          handleSubmit={this.handleCancelReservation}
        >
          <TripCardLeft
            trip={trip}
            user={user}
            theme={theme}
            serviceId={serviceId}
            simpleRequestType={simpleRequestType}
            hideFooter
            registeredCalendarEvents={registeredCalendarEvents}
            isReceipt={isReceipt}
          />
        </CancelTripModal>
        {trip.status === 0 && showRequestApproval && (
          <RequestApprovalModal
            show={showRequestApproval}
            handleHide={this.hideRequestApprovalModal}
            handleSubmit={this.submitRequestApproval}
            trip={trip}
          >
            <TripCardLeft
              trip={trip}
              user={user}
              theme={theme}
              serviceId={serviceId}
              simpleRequestType={simpleRequestType}
              showItemCancelModal={this.showItemCancelModal}
              hideFooter={false}
              registeredCalendarEvents={registeredCalendarEvents}
              isReceipt={isReceipt}
            />
          </RequestApprovalModal>
        )}
        <FlightSeatMapModal
          trip={trip}
          show={showFlightSeatMapModal}
          onHide={() => {
            this.setState({ showFlightSeatMapModal: false });
          }}
        />
        <CalendarSuccessModal
          provider="google"
          trip={trip}
          onEventRegistered={this.handleCalendarEventRegistered}
        />
        <CalendarSuccessModal
          provider="outlook"
          trip={trip}
          onEventRegistered={this.handleCalendarEventRegistered}
        />
      </SimpleCardWrap>
    );
  }
}

const SimpleCardWrap = styled.div`
  overflow: hidden;
  margin-bottom: 25px;
  border-radius: 5px;
`;

const SimpleCard = styled.div`
  background: #fff;
  transition: margin ease-in 0.3s;
`;

const SimpleCardBody = styled.div`
  display: flex;
  background: #fff;
  border-radius: 5px;
  font-size: 12px;

  ${media.sp`
    display: block;
  `}
`;

const CardMain = styled.div`
  flex-grow: 1;
`;

const CardHead = styled.div`
  width: 100%;
  display: flex;
  padding: 10px 20px;
  border-bottom: solid 1px ${props => props.theme.grayBorderColor};
  @media screen and (max-width: 767px) {
    flex-wrap: wrap;
  }
`;

const CardContent = styled.div`
  padding: 15px;
  background-color: #f7f7f7;
`;

const MainInfo = styled.div`
  padding-bottom: 20px;
`;

const Bold = styled.span`
  font-weight: bold;
`;

const Red = styled.p`
  color: ${props => props.theme.redColor};
`;

const CardSub = styled.div`
  min-width: 300px;
  width: 300px;
  padding: 15px 20px;
  background: #ffffff;
  border-top: solid 1px ${props => props.theme.grayBorderColor};

  ${media.sp`
    min-width: 100%;
    width: 100%;
  `}
`;

const SubBlock = styled.div`
  margin-bottom: 20px;
`;

const SubTitle = styled.p`
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: bold;

  ${media.sp`
    font-size: 12px;
  `}
`;

const SubTable = styled.table`
  width: auto;
  margin: 0;
  border: none;
  font-size: 11px;
  vertical-align: top;

  ${media.sp`
    font-size: 10px;
  `}
`;

const SubTd = styled.td`
  padding: 4px 0;
  border: none;
  vertical-align: top;

  ${media.sp`
    padding: 3px 0;
  `}
`;

const SubTdData = styled(SubTd)`
  width: 68px;
`;

const SubTdColon = styled(SubTd)`
  width: 15px;
`;

export default withTheme(TripCard);
